import type { JSONContent } from "@tiptap/core";

export type MessageFormat = "plain" | "rich-text";
export const mentionPattern = /\{\{@([a-zA-Z0-9]{1,10})\}\}/g;

export function serializeMessageDocument(node: JSONContent): string {
	function stored(value: JSONContent): JSONContent {
		if (value.type === "mention")
			return {
				type: "text",
				text: `{{@${String(value.attrs?.id).toUpperCase()}}}`,
				...(value.marks ? { marks: value.marks } : {}),
			};
		return {
			...value,
			...(value.content ? { content: value.content.map(stored) } : {}),
		};
	}
	return JSON.stringify(stored(node));
}

export function messageToDocument(
	message: string,
	format: MessageFormat = "plain",
): JSONContent {
	let document: JSONContent;
	if (format === "rich-text" && message) {
		try {
			document = JSON.parse(message);
			if (document.type !== "doc") throw new Error("Invalid document");
		} catch {
			document = plainDocument(message);
		}
	} else document = plainDocument(message);
	function hydrate(node: JSONContent): JSONContent[] {
		if (node.type === "text" && node.text) {
			const pieces: JSONContent[] = [];
			let index = 0;
			for (const match of node.text.matchAll(mentionPattern)) {
				if (match.index > index)
					pieces.push({ ...node, text: node.text.slice(index, match.index) });
				const initials = match[1].toUpperCase();
				pieces.push({
					type: "mention",
					attrs: { id: initials, label: initials },
					...(node.marks ? { marks: node.marks } : {}),
				});
				index = match.index + match[0].length;
			}
			if (index < node.text.length)
				pieces.push({ ...node, text: node.text.slice(index) });
			return pieces;
		}
		return [
			{
				...node,
				...(node.content ? { content: node.content.flatMap(hydrate) } : {}),
			},
		];
	}
	return hydrate(document)[0];
}

function plainDocument(message: string): JSONContent {
	return {
		type: "doc",
		content: message
			.split(/\r?\n/)
			.map((text) => ({
				type: "paragraph",
				content: text ? [{ type: "text", text }] : [],
			})),
	};
}

export function hasMessageContent(
	message: string,
	format: MessageFormat = "rich-text",
): boolean {
	if (format === "plain") return !!message.trim();
	if (!message) return false;
	try {
		const document: JSONContent = JSON.parse(message);
		function hasText(node: JSONContent): boolean {
			return (
				!!node.text?.trim() ||
				node.type === "mention" ||
				!!node.content?.some(hasText)
			);
		}
		return hasText(document);
	} catch {
		return false;
	}
}

export function safeMessageLink(value: unknown): value is string {
	return (
		typeof value === "string" &&
		![...value].some(
			(char) =>
				char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127 || char === "\\",
		) &&
		(/^(https?:\/\/|mailto:)/i.test(value) ||
			(value.startsWith("/") && !value.startsWith("//")) ||
			value.startsWith("#"))
	);
}
